import { ShutterFields } from "@/components/ShutterFields";
import { FieldError } from "@/components/States";
import { MeterDetailsFields } from "@/components/MeterDetailsFields";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ENGINEER_JOB_TYPES, TEAM_ROLE_LABELS, WORKER_ROLE_OPTIONS } from "@/lib/constants";

const RoleFields = ({ role, value, onChange, testId, engineers, dogHandlers, runEngineerId, allowEngineerAssignment, jobTypes, meterDetailsError }) => (
  <div className="space-y-3">
    {role === "agent" && (
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor={`${testId}-customer-name`}>Customer Name</Label>
          <Input id={`${testId}-customer-name`} value={value.customer_name} onChange={(e) => onChange({ customer_name: e.target.value })} maxLength={120} className="mt-1" data-testid={`${testId}-customer-name`} />
        </div>
        <div>
          <Label htmlFor={`${testId}-customer-telephone`}>Customer Telephone</Label>
          <Input id={`${testId}-customer-telephone`} type="tel" value={value.customer_telephone} onChange={(e) => onChange({ customer_telephone: e.target.value })} maxLength={40} className="mt-1" data-testid={`${testId}-customer-telephone`} />
        </div>
        <div>
          <Label htmlFor={`${testId}-debt-amount`}>Debt Amount</Label>
          <Input id={`${testId}-debt-amount`} type="number" min="0" step="0.01" value={value.debt_amount} onChange={(e) => onChange({ debt_amount: e.target.value })} className="mt-1" data-testid={`${testId}-debt-amount`} />
        </div>
        <div>
          <Label htmlFor={`${testId}-previous-visits`}>Previous Visits</Label>
          <Input id={`${testId}-previous-visits`} type="number" min="0" step="1" value={value.previous_visits} onChange={(e) => onChange({ previous_visits: e.target.value })} className="mt-1" data-testid={`${testId}-previous-visits`} />
        </div>
        <div>
          <Label htmlFor={`${testId}-vulnerability`}>Vulnerability</Label>
          <Textarea id={`${testId}-vulnerability`} rows={2} value={value.vulnerability_text} onChange={(e) => onChange({ vulnerability_text: e.target.value })} maxLength={2000} className="mt-1" data-testid={`${testId}-vulnerability`} />
        </div>
        <div>
          <Label htmlFor={`${testId}-notes`}>Notes</Label>
          <Textarea id={`${testId}-notes`} rows={2} value={value.agent_notes} onChange={(e) => onChange({ agent_notes: e.target.value })} maxLength={2000} className="mt-1" data-testid={`${testId}-notes`} />
        </div>
      </div>
    )}
    {role === "locksmith" && <>
      <ShutterFields value={value} onChange={onChange} testId={`${testId}-shutter`} />
      <div>
        <Label htmlFor={`${testId}-notes`}>Notes</Label>
        <Textarea id={`${testId}-notes`} rows={2} value={value.notes} onChange={(e) => onChange({ notes: e.target.value })} maxLength={2000} className="mt-1" data-testid={`${testId}-notes`} />
      </div>
    </>}
    {role === "engineer" && <>
      <div>
        <Label htmlFor={`${testId}-engineer-type`}>Job Type</Label>
        <Select value={value.engineer_type || ""} onValueChange={(engineer_type) => onChange({ engineer_type })}>
          <SelectTrigger id={`${testId}-engineer-type`} className="mt-1" data-testid={`${testId}-engineer-type`}><SelectValue placeholder="Select job type" /></SelectTrigger>
          <SelectContent>
            {[...new Set([...(jobTypes ?? ENGINEER_JOB_TYPES), ...(value.engineer_type ? [value.engineer_type] : [])])].map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {allowEngineerAssignment && !runEngineerId && <div>
        <Label htmlFor={`${testId}-engineer-assignee`}>Engineer for this address</Label>
        <Select value={value.engineer_id || "unassigned"} onValueChange={(engineer_id) => onChange({ engineer_id: engineer_id === "unassigned" ? null : engineer_id })}>
          <SelectTrigger id={`${testId}-engineer-assignee`} className="mt-1" data-testid={`${testId}-engineer-assignee`}><SelectValue placeholder="Select Engineer" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {engineers.filter((engineer) => engineer.active || engineer.id === value.engineer_id).map((engineer) => (
              <SelectItem key={engineer.id} value={engineer.id}>{engineer.full_name || engineer.email}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>}
      <fieldset>
        <legend className="text-sm font-semibold text-slate-900">Meter Details</legend>
        <div className="mt-1"><MeterDetailsFields value={value.meter_details} onChange={(meter_details) => onChange({ meter_details })} testId={`${testId}-meter`} /></div>
        <FieldError message={meterDetailsError} testId={`${testId}-meter-error`} />
      </fieldset>
      <div>
        <Label htmlFor={`${testId}-notes`}>Notes</Label>
        <Textarea id={`${testId}-notes`} rows={2} value={value.engineer_notes} onChange={(e) => onChange({ engineer_notes: e.target.value })} maxLength={2000} className="mt-1" data-testid={`${testId}-notes`} />
      </div>
    </>}
    {role === "dog_handler" && (
      <>
        {allowEngineerAssignment && <div>
          <Label htmlFor={`${testId}-assignee`}>Dog Handler for this address</Label>
          <Select value={value.dog_handler_id || "unassigned"} onValueChange={(dog_handler_id) => onChange({ dog_handler_id: dog_handler_id === "unassigned" ? null : dog_handler_id })}>
            <SelectTrigger id={`${testId}-assignee`} className="mt-1" data-testid={`${testId}-assignee`}><SelectValue placeholder="Select Dog Handler" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {dogHandlers.filter((handler) => handler.active || handler.id === value.dog_handler_id).map((handler) => (
                <SelectItem key={handler.id} value={handler.id}>{handler.full_name || handler.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>}
        <div>
          <Label htmlFor={`${testId}-notes`}>Notes</Label>
          <Textarea id={`${testId}-notes`} rows={2} value={value.dog_handler_notes} onChange={(e) => onChange({ dog_handler_notes: e.target.value })} maxLength={2000} className="mt-1" data-testid={`${testId}-notes`} />
        </div>
      </>
    )}
  </div>
);

export const AddressRoleFields = ({ value, onChange, warrantTypes = [], testId, engineers = [], dogHandlers = [], runEngineerId = "", allowEngineerAssignment = false, jobTypes = ENGINEER_JOB_TYPES, meterDetailsError }) => {
  const fieldRoles = ["agent", "engineer", "locksmith", "dog_handler"]
    .filter((role) => warrantTypes.includes(role) && WORKER_ROLE_OPTIONS.some(([workerRole]) => workerRole === role));
  const locksmithOnly = fieldRoles.length === 1 && fieldRoles[0] === "locksmith" && warrantTypes.length === 1;

  if (!fieldRoles.length) return null;
  if (locksmithOnly) return <RoleFields role="locksmith" value={value} onChange={onChange} testId={testId} />;

  return (
    <Tabs defaultValue={fieldRoles[0]} className="w-full">
      <TabsList className="grid h-auto w-full grid-cols-2 gap-1.5 rounded-xl border border-slate-200 bg-slate-100/80 p-1.5 sm:flex sm:flex-wrap sm:justify-start">
        {fieldRoles.map((role) => <TabsTrigger key={role} value={role} className="min-h-10 gap-2 px-3 text-xs font-semibold text-slate-600 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm sm:flex-1 sm:px-4 sm:text-sm" data-testid={`${testId}-role-tab-${role}`}>{TEAM_ROLE_LABELS[role]}</TabsTrigger>)}
      </TabsList>
      {fieldRoles.map((role) => (
        <TabsContent key={role} value={role} data-testid={`${testId}-role-panel-${role}`}>
          <RoleFields role={role} value={value} onChange={onChange} testId={`${testId}-${role}`} engineers={engineers} dogHandlers={dogHandlers} runEngineerId={runEngineerId} allowEngineerAssignment={allowEngineerAssignment} jobTypes={jobTypes} meterDetailsError={meterDetailsError} />
        </TabsContent>
      ))}
    </Tabs>
  );
};